/**
 * The wiring behind the Przegląd's „Wymagają reakcji" Talia: which of the client's tasks is open,
 * and every write a card on it can make.
 *
 * The cards are `features/attention-queue`'s — the same content `/zadania` and the Pulpit draw — so
 * what is here is only what this surface owns: the answers („Później", „Gotowe") through the one
 * responder, „Oznacz jako przejrzane" through `features/survey-review`, and the writes the plan, goal and invitation cards make, which are three other
 * features' hooks spread into the card's one slot (a feature may not import a feature, so they meet
 * here). A clip is not written from here: its card links to the one player (`?tab=forma&clip=`),
 * which marks it seen.
 *
 * ── Nothing is struck here; the queue's next answer is the whole close ───────────────────────
 * Every write above invalidates `GET /attention-queue` (or answers it optimistically), and this
 * section is drawn from that read, so a resolved task leaves with the answer that says it is
 * resolved. `onResolved` has nothing to add.
 *
 * ── Every write is reported to the landing ───────────────────────────────────────────────────
 * Any of them can take the open card away with the control that was pressed, so each one tells
 * `useTaliaLanding` when it starts, and whether it was accepted or refused. The in-card writes are
 * wrapped once, here, so a card cannot gain a write that forgets to.
 */

import { useRef, useState, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import {
  snoozeUntil,
  taskLabel,
  useAttentionResponse,
  type AttentionItem,
  type AttentionResponseKind,
  type TaskSlots,
} from '~/features/attention-queue';
import { useGoalWrites } from '~/features/client-goal';
import { useResendInvitation } from '~/features/invite-client';
import { usePlanLifecycle } from '~/features/plan-lifecycle';
import { useMarkSurveyReviewed } from '~/features/survey-review';

import { useTaliaLanding } from './useTaliaLanding';

export interface OverviewTasksState {
  /** The open card's item — the chosen tab, or the first task once the chosen one has left. */
  readonly selected: AttentionItem | null;
  readonly select: (key: string) => void;
  readonly answer: (item: AttentionItem, kind: AttentionResponseKind) => void;
  readonly markReviewed: (item: AttentionItem, surveyAssignmentId: number) => void;
  /** The writes and the reads the card's content asks the surface for. `survey` is the page's. */
  readonly writes: TaskSlots['writes'];
  readonly announce: (message: string) => void;
  /** The open card's heading — where the keyboard lands when one task is left. */
  readonly headingRef: RefObject<HTMLSpanElement | null>;
  /** The up-to-date line — where the keyboard lands when the last task is answered. */
  readonly upToDateRef: RefObject<HTMLDivElement | null>;
}

/**
 * @param queueUpdatedAt when the queue read `tasks` come from last answered — see `useTaliaLanding`.
 * @param announce the client card's ONE live region (`ui/cardAnnouncer`).
 */
export function useOverviewTasks(
  tasks: readonly AttentionItem[],
  queueUpdatedAt: number,
  announce: (message: string) => void,
): OverviewTasksState {
  const { t } = useTranslation();
  const responder = useAttentionResponse({ announce });
  // The attention queue is the only other read the mark touches, and the hook invalidates it itself.
  const review = useMarkSurveyReviewed({ invalidates: [] });

  const [chosenKey, setChosenKey] = useState<string | null>(null);
  const selected = tasks.find((item) => item.key === chosenKey) ?? tasks[0] ?? null;
  const headingRef = useRef<HTMLSpanElement>(null);
  const upToDateRef = useRef<HTMLDivElement>(null);
  const select = (key: string) => {
    setChosenKey(key);
  };
  const landing = useTaliaLanding({
    tasks,
    selected,
    queueUpdatedAt,
    outcome: responder.outcome,
    choose: select,
    headingRef,
    upToDateRef,
  });

  /** One of the card's own writes, reported to the landing. The card only ever acts on `selected`. */
  const reported =
    <Args extends unknown[]>(write: (...args: Args) => Promise<void>) =>
    (...args: Args): Promise<void> => {
      const key = selected?.key;
      if (key === undefined) return write(...args);
      landing.writing(key);
      const sent = write(...args);
      void sent.then(
        () => {
          landing.accepted(key);
        },
        () => {
          landing.refused(key);
        },
      );
      return sent;
    };
  const plan = usePlanLifecycle();
  const goal = useGoalWrites();
  const invitation = useResendInvitation();
  const writes: TaskSlots['writes'] = {
    addWeek: reported(plan.addWeek),
    completePlan: reported(plan.completePlan),
    shiftRemaining: reported(plan.shiftRemaining),
    extendGoal: reported(goal.extendGoal),
    completeGoal: reported(goal.completeGoal),
    resendInvitation: reported(invitation.resendInvitation),
  };

  return {
    selected,
    select,
    answer: (item, kind) => {
      landing.answering(item, kind);
      responder.respond({
        key: item.key,
        anchorKey: item.anchorKey,
        reason: item.reason,
        kind,
        subjectCustomerId: item.subjectCustomerId,
        task: taskLabel(item, t),
        // This surface offers no choice of term, so it writes the default one — the Pulpit's rule.
        snoozeUntilIso: kind === 'Snoozed' ? snoozeUntil(new Date()) : null,
      });
    },
    markReviewed: (item, surveyAssignmentId) => {
      // The task leaves only when the server answers and the queue is read again; a refusal keeps
      // it, and the pressed control with it.
      landing.writing(item.key);
      review.markReviewed(
        surveyAssignmentId,
        () => {
          landing.accepted(item.key);
          announce(t('web:home.live.reviewed', { task: taskLabel(item, t) }));
        },
        () => {
          landing.refused(item.key);
        },
      );
    },
    writes,
    announce,
    headingRef,
    upToDateRef,
  };
}
